import type { Lang } from '../../lib/i18n'

// Screen-local dictionary — kept alongside the feature (not the shared src/locales/*.json),
// mirroring features/orders/orders-i18n.ts so this bench stays inside files_locked.
const EN = {
  title: 'Order Board',
  loading: 'Loading orders…',
  errorLoading: "Couldn't load orders.",
  retry: 'Retry',
  empty: 'No orders in this view.',

  statusAll: 'All',

  // D5 aging badge — icon + numeric + label, never color-alone.
  agingWaiting: 'Waiting',
  agingDue: 'Due in',
  agingOverdue: 'Overdue',
  agingScheduled: 'Scheduled',

  // D4 new-since-last-look.
  newBadge: 'New',

  delivery: 'Delivery',
  pickup: 'Pickup',
  testOrder: 'TEST',
  items: 'items',
  item: 'item',

  // D7(a) read-only dispatch/cash badges.
  dispatchDriverAssigned: 'Driver assigned',
  dispatchCashCollected: 'Cash collected',
  dispatchCashToCollect: 'Cash to collect',
  dispatchException: 'Delivery exception',

  // D2 admin-only Reject (Popover reason, not a dialog).
  reject: 'Reject',
  rejectReasonLabel: 'Reason',
  rejectReasonPlaceholder: 'Reason for rejecting this order…',
  rejectSubmit: 'Reject order',
  rejectCancel: 'Cancel',

  // R197 (b) one-click final check. The button's own label is reused from orders-i18n
  // FINAL_CHECK_ACTION_LABEL (one action, one Arabic). {slots} are filled by fillSlots.
  finalCheckConfirmTitle: 'Approve this order?',
  finalCheckChanged: 'The total changed from {from} to {to}.',
  finalCheckUnreadable: 'The total may have changed. Open the order to check it.',
  finalCheckApprove: 'Approve',
  finalCheckCancel: 'Cancel',
  finalCheckSending: 'Sending…',
  finalCheckQueued: 'Queued, sends when online',
  finalCheckRejected: 'Not approved: {message}',

  // R264 (b): a move waits for the server and names the order. The AR values of these seven
  // keys are English stubs; Rook's commit on the lane branch puts the Arabic in (R197 (b) precedent).
  advanceFailed: 'Order {code} was not moved: {message}',
  statusUpdatedFor: 'Order {code}: status updated',
  kitchenOpen: 'Order {code}: {count} meal item(s) not finished in the kitchen',
  moveAnyway: 'Move anyway',
  dismiss: 'Dismiss',
  sending: 'Sending...',
  queuedRefused: 'Order {code}: not moved, the server refused it ({status})',

  // ux2 (b): the card footer's full-width way into the order.
  open: 'Open',
}

const AR: typeof EN = {
  title: 'لوحة الطلبات',
  loading: 'جارٍ تحميل الطلبات…',
  errorLoading: 'تعذّر تحميل الطلبات.',
  retry: 'إعادة المحاولة',
  empty: 'لا طلبات في هذا العرض.',

  statusAll: 'الكل',

  agingWaiting: 'قيد الانتظار',
  agingDue: 'مستحق خلال',
  agingOverdue: 'متأخر',
  agingScheduled: 'مجدوَل',

  newBadge: 'جديد',

  delivery: 'توصيل',
  pickup: 'استلام',
  testOrder: 'تجريبي',
  items: 'عناصر',
  item: 'عنصر',

  dispatchDriverAssigned: 'تم تعيين سائق',
  dispatchCashCollected: 'تم تحصيل النقد',
  dispatchCashToCollect: 'نقد قيد التحصيل',
  dispatchException: 'استثناء في التوصيل',

  reject: 'رفض',
  rejectReasonLabel: 'السبب',
  rejectReasonPlaceholder: 'سبب رفض هذا الطلب…',
  rejectSubmit: 'رفض الطلب',
  rejectCancel: 'إلغاء',

  // R197 (b): STUBS. Every value below is the English text; Rook's one commit to this file
  // puts the Arabic in (round-2 carry). The has-Arabic cell (i2) is red until then, by design.
  finalCheckConfirmTitle: 'هل توافق على هذا الطلب؟',
  finalCheckChanged: 'تغيّر المجموع من {from} إلى {to}.',
  finalCheckUnreadable: 'ربما تغيّر المجموع. افتح الطلب للتحقق.',
  finalCheckApprove: 'موافقة',
  finalCheckCancel: 'إلغاء',
  finalCheckSending: 'جارٍ الإرسال…',
  finalCheckQueued: 'في الانتظار، يُرسل عند الاتصال',
  finalCheckRejected: 'لم تتم الموافقة: {message}',

  // R264 (b): Arabic by Rook's commit on the lane (R197 (b) precedent); {slots} as in the EN block.
  advanceFailed: 'الطلب {code} ما انتقل: {message}',
  statusUpdatedFor: 'الطلب {code}: تم تحديث الحالة',
  kitchenOpen: 'الطلب {code}: {count} وجبة لم تكتمل في المطبخ',
  moveAnyway: 'انقله على أي حال',
  dismiss: 'إغلاق',
  sending: 'جارٍ الإرسال...',
  queuedRefused: 'الطلب {code}: ما انتقل، الخادم رفضه ({status})',

  // ux2 (b): Arabic by Rook, copied byte for byte from vault team/ux-form-pass-2026-10-30/hop-ar-strings.md.
  open: 'فتح',
}

const BOARD_DICT = { en: EN, ar: AR } as const

// Exposed for board-i18n.test.ts (EN/AR key and slot parity, i2).
export const BOARD_DICT_FOR_TEST = BOARD_DICT

// Replaces each `{name}` once, so a language can place `{from}` and `{to}` in its own order.
// An unknown name is left as written.
export function fillSlots(text: string, slots: Record<string, string>): string {
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => (name in slots ? slots[name] : whole))
}

export function bt<K extends keyof typeof EN>(lang: Lang, key: K): (typeof EN)[K] {
  return BOARD_DICT[lang][key]
}
